import { Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { SimpleMailOutputNode } from 'pages/assistants/builder/components/nodes/simpleMailOutputNode';
import { BaseNode } from 'pages/assistants/builder/components/nodes/baseNode';
import { PromptNode } from 'pages/assistants/builder/components/nodes/promptNode';
import { KnowledgeBaseNode } from 'pages/assistants/builder/components/nodes/knowledgeBaseNode';
import { LogAdvisorNode as LogAdvisorCompNode } from 'pages/assistants/builder/components/nodes/logAdvisorNode';
import { AdvisorNode } from 'pages/assistants/builder/components/nodes/advisorNode';
import { ModelNode } from 'pages/assistants/builder/components/nodes/modelNode';
import { OutputNode } from 'pages/assistants/builder/components/nodes/outputNode';
import { LlmAdvisorNode } from 'pages/assistants/builder/components/nodes/llmAdvisorNode';
import { GroovyNode } from 'pages/assistants/builder/components/nodes/groovyNode';
import { ClassificationNode } from 'pages/assistants/builder/components/nodes/classificationNode';
import { SwitcherNode } from 'pages/assistants/builder/components/nodes/switcherNode';
import { SimpleMailInputNode } from 'pages/assistants/builder/components/nodes/simpleMailInputNode';
import { AccountNode } from 'pages/assistants/builder/components/nodes/accountNode';
import { LongContextModelNode } from 'pages/assistants/builder/components/nodes/longContextModelNode';
import { InputNode } from 'pages/assistants/builder/components/nodes/InputNode';

const MemoizedBaseNode = memo(BaseNode);
const MemoizedMailInputNode = memo(SimpleMailInputNode);
const MemoizedMailOutputNode = memo(SimpleMailOutputNode);
const MemoizedPromptNode = memo(PromptNode);
const MemoizedKnowledgeBaseNode = memo(KnowledgeBaseNode);
const MemoizedAdvisorNode = memo(AdvisorNode);
const MemoizedLlmAdvisorNode = memo(LlmAdvisorNode);
const MemoizedLogAdvisorNode = memo(LogAdvisorCompNode);
const MemoizedModelNode = memo(ModelNode);
const MemoizedLongContextModelNode = memo(LongContextModelNode);
const MemoizedGroovyNode = memo(GroovyNode);
const MemoizedClassificationNode = memo(ClassificationNode);
const MemoizedSwitcherNode = memo(SwitcherNode);

const MemoizedOutputNode = memo(OutputNode);
const MemoizedAccountNode = memo(AccountNode);

const UserInputNode = memo((props) => (
  <InputNode
    icon="person_icon"
    {...props}
    nodeData={props.data}
    color="infoBlue"
    handleConf={[{ type: 'source', position: Position.Right }]}
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
  />
));

const MailInputNode = memo((props) => (
  <MemoizedMailInputNode
    icon="mail_icon"
    {...props}
    nodeData={props.data}
    color="infoBlue"
    handleConf={[
      { type: 'source', position: Position.Right },
      { type: 'target', position: Position.Bottom, style: { background: '#c47e7e' } }
    ]}
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
  />
));

const MailOutputNode = memo((props) => (
  <MemoizedMailOutputNode
    icon="mail_icon"
    {...props}
    nodeData={props.data}
    color="infoBlue"
    handleConf={[
      { type: 'target', position: Position.Left },
      { type: 'source', position: Position.Right },
      { type: 'target', position: Position.Bottom, style: { background: '#c47e7e' } }
    ]}
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
  />
));

const AccountConnexionNode = memo((props) => (
  <MemoizedAccountNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[{ type: 'source', position: Position.Top, style: { backgroundColor: '#c47e7e' } }]}
  />
));

const UserOutputNode = memo((props) => (
  <MemoizedOutputNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[{ type: 'target', position: Position.Left }]}
  />
));

const RewriteAdvisorNode = memo((props) => (
  <MemoizedLlmAdvisorNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[
      { type: 'target', position: Position.Left },
      { type: 'target', position: Position.Top, style: { backgroundColor: 'orange' } },
      { type: 'source', position: Position.Right }
    ]}
  />
));

const MemoryAdvisorNode = memo((props) => (
  <MemoizedAdvisorNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[
      { type: 'target', position: Position.Top, style: { backgroundColor: 'orange' } },
      { type: 'target', position: Position.Left },
      { type: 'source', position: Position.Right }
    ]}
  />
));

const DocumentAdvisorNode = memo((props) => (
  <MemoizedLlmAdvisorNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[
      { type: 'target', position: Position.Top, style: { backgroundColor: 'orange' } },
      { type: 'target', position: Position.Left },
      { type: 'target', position: Position.Bottom, style: { backgroundColor: '#334667' } },
      { type: 'source', position: Position.Right }
    ]}
  />
));

const LogAdvisorNode = memo((props) => (
  <MemoizedLogAdvisorNode
    onEditNode={props.onEditNode}
    onDeleteNode={props.onDeleteNode}
    {...props}
    handleConf={[
      { type: 'target', position: Position.Left },
      { type: 'source', position: Position.Right }
    ]}
  />
));

/**
 * Hook to create node components
 * @returns {object} Map of node type names to React components
 */
export const assistantNodeTypes = (handleEditNode, handleDeleteNode) => ({
  userInput: (props) => (
    <UserInputNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  mailInput: (props) => (
    <MailInputNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  prompt: (props) => (
    <MemoizedPromptNode onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} {...props} />
  ),

  rewriteAdvisor: (props) => (
    <RewriteAdvisorNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  memoryAdvisor: (props) => (
    <MemoryAdvisorNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  documentAdvisor: (props) => (
    <DocumentAdvisorNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  logAdvisor: (props) => (
    <LogAdvisorNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  userOutput: (props) => (
    <UserOutputNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  mailOutput: (props) => (
    <MailOutputNode {...props} onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} />
  ),

  model: (props) => (
    <MemoizedModelNode onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} {...props} />
  ),

  longContextModel: (props) => (
    <MemoizedLongContextModelNode
      onEditNode={handleEditNode}
      onDeleteNode={handleDeleteNode}
      {...props}
    />
  ),

  knowledgeBase: (props) => (
    <MemoizedKnowledgeBaseNode
      onEditNode={handleEditNode}
      onDeleteNode={handleDeleteNode}
      {...props}
    />
  ),

  account: (props) => (
    <AccountConnexionNode onEditNode={handleEditNode} onDeleteNode={handleDeleteNode} {...props} />
  ),

  groovyProcessor: (props) => (
    <MemoizedGroovyNode
      onEditNode={handleEditNode}
      onDeleteNode={handleDeleteNode}
      {...props}
      handleConf={[
        { type: 'target', position: Position.Top, style: { background: 'orange' } },
        { type: 'target', position: Position.Bottom, style: { background: '#c47e7e' } },
        { type: 'target', position: Position.Left },
        { type: 'source', position: Position.Right }
      ]}
    />
  ),

  tools: (props) => (
    <MemoizedBaseNode
      icon={props.data.icon}
      nodeData={props.data}
      color="tools"
      {...props}
      handleConf={[
        { type: 'source', position: Position.Top, style: { background: '#8c8b95' } },
        { type: 'target', position: Position.Bottom, style: { background: '#c47e7e' } }
      ]}
      onDeleteNode={handleDeleteNode}
    />
  ),

  classificationProcessor: (props) => (
    <MemoizedClassificationNode
      onEditNode={handleEditNode}
      onDeleteNode={handleDeleteNode}
      {...props}
    />
  ),

  switcherProcessor: (props) => (
    <MemoizedSwitcherNode
      onEditNode={handleEditNode}
      onDeleteNode={handleDeleteNode}
      {...props}
      handleConf={[{ id: Position.Left, type: 'target', position: Position.Left }]}
    />
  )
});

export const isValidConnectionNodes = (nodes, connection) => {
  const getHandlePosition = (node, handleId) => {
    if (!node || !handleId) return null;
    const handleInfo = node.data?.handleConf?.find((h) => h.id === handleId);
    return handleInfo?.position || handleId;
  };

  const sourceNode = nodes.find((n) => n.id === connection.source);
  const targetNode = nodes.find((n) => n.id === connection.target);

  const sourcePosition = getHandlePosition(sourceNode, connection.sourceHandle);
  const targetPosition = getHandlePosition(targetNode, connection.targetHandle);

  // Extract node types from IDs (assuming format "type_id")
  const sourceType = sourceNode.type;
  const targetType = targetNode.type;

  console.debug('🔍 DEBUG CONNECTION INFOS START');
  console.debug('🧩 Connection object:', connection);
  console.debug('➡️ Source node ID:', connection.source);
  console.debug('⬅️ Target node ID:', connection.target);
  console.debug('🧱 Source node:', sourceNode);
  console.debug('🎯 Target node:', targetNode);
  console.debug('🔘 Source handle:', connection.sourceHandle);
  console.debug('🔘 Target handle:', connection.targetHandle);
  console.debug('📍 Source position:', sourcePosition);
  console.debug('📍 Target position:', targetPosition);
  console.debug('🔠 Source type:', sourceType);
  console.debug('🔡 Target type:', targetType);
  console.debug('🔍 DEBUG CONNECTION INFOS END');

  // ADVISOR NODE constraints:
  // - Left: can be connected from userInput or another advisor
  // - Top: can be connected from a prompt
  // - Right: can connect to userOutput or another advisor

  if (sourceType === 'tools') {
    return targetType === 'model' && targetPosition === 'bottom';
  }

  if (sourceType === 'account') {
    return (
      (targetType === 'mailInput' ||
        targetType === 'mailOutput' ||
        targetType === 'groovyProcessor' ||
        targetType === 'tools') &&
      targetPosition === 'bottom'
    );
  }

  if (isProcessor(targetType)) {
    if (targetPosition === 'left') {
      return sourcePosition === 'right';
    }

    if (targetPosition === 'top') {
      return sourceType === 'prompt';
    }

    if (targetPosition === 'bottom') {
      return sourceType === 'knowledgeBase';
    }

    // Reject connections to other handles of advisor
    return false;
  }

  // Connections FROM advisor (source)
  if (isProcessor(sourceType)) {
    // Advisor can only connect from its right handle
    if (sourcePosition !== 'right') {
      return false;
    }

    // Advisor's right handle can connect to another advisor's left or to userOutput
    return (
      ((isProcessor(targetType) || targetType === 'model') && targetPosition === 'left') ||
      targetType === 'userOutput'
    );
  }

  if (targetType === 'model') {
    // Left handle connections to advisor
    if (targetPosition === 'left') {
      return sourceType === 'userInput' || isProcessor(sourceType);
    }

    // Top handle connections to advisor
    if (targetPosition === 'top') {
      return sourceType === 'prompt';
    }

    // Reject connections to other handles of advisor
    return false;
  }

  // Connections FROM advisor (source)
  if (sourceType === 'model') {
    // Advisor can only connect from its right handle
    if (sourcePosition !== 'right') {
      return false;
    }

    // Advisor's right handle can connect to another advisor's left or to userOutput
    return targetType === 'userOutput' || isProcessor(targetType);
  }

  // PROMPT NODE constraints
  if (sourceType === 'prompt') {
    // Prompt can only connect to advisor's top handle
    return isProcessor(targetType) && targetPosition === 'top';
  }

  // USER INPUT NODE constraints
  if (sourceType === 'userInput') {
    // UserInput can only connect to advisor's left handle
    return isProcessor(targetType) && targetPosition === 'left';
  }

  // USER OUTPUT NODE constraints
  if (targetType === 'userOutput') {
    // UserOutput can only be connected from advisor's right handle
    return isProcessor(sourceType) && sourcePosition === 'right';
  }

  // Reject any other connection type
  return false;
};

function isProcessor(type) {
  const advisorTypes = [
    'classificationProcessor',
    'documentAdvisor',
    'logAdvisor',
    'longContextModel',
    'groovyProcessor',
    'mailOutput',
    'memoryAdvisor',
    'model',
    'rewriteAdvisor',
    'switcherProcessor'
  ];

  return advisorTypes.includes(type);
}
/*
export const AdvisorCategoryCode = Object.freeze({
  MEMORY: 'MEMORY',
  DOCUMENT: 'DOCUMENT',
  LOG: 'LOG',
  PERSISTENCE: 'PERSISTENCE',
  REWRITE: 'REWRITE',
  CLASSIFICATION: 'CLASSIFICATION'
});
    */
UserInputNode.propTypes = {
  data: PropTypes.object.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

UserOutputNode.propTypes = {
  data: PropTypes.object.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

MemoryAdvisorNode.propTypes = {
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

RewriteAdvisorNode.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string,
    scriptName: PropTypes.string,
    isSynch: PropTypes.bool
  }),
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

DocumentAdvisorNode.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string
  }),
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

LogAdvisorNode.propTypes = {
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

MailInputNode.propTypes = {
  data: PropTypes.object.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

AccountConnexionNode.propTypes = {
  data: PropTypes.object.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};

MailOutputNode.propTypes = {
  data: PropTypes.object.isRequired,
  onEditNode: PropTypes.func.isRequired,
  onDeleteNode: PropTypes.func.isRequired
};
